import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, FolderKanban, Users, Send, Copy, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_COLS = [
  { id: "todo", label: "À faire", color: "bg-gray-100", text: "text-gray-700" },
  { id: "in_progress", label: "En cours", color: "bg-blue-50", text: "text-blue-700" },
  { id: "review", label: "En révision", color: "bg-amber-50", text: "text-amber-700" },
  { id: "done", label: "Livré", color: "bg-green-50", text: "text-green-700" },
];

const PROJ_STATUS = [
  { v: "active", label: "Actif" },
  { v: "on_hold", label: "En pause" },
  { v: "completed", label: "Terminé" },
];

const DELIV_STATUS_BADGE = {
  pending: "bg-orange-100 text-orange-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

export default function ProjectsPanel() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({ name: "", client_email: "", description: "", status: "active" });
  const [openProjId, setOpenProjId] = useState(null);

  const [creatingClient, setCreatingClient] = useState(false);
  const [clientForm, setClientForm] = useState({ name: "", email: "", company: "", phone: "" });
  const [createdCreds, setCreatedCreds] = useState(null);

  const loadAll = async () => {
    try {
      const [p, c] = await Promise.all([api.get("/admin/projects"), api.get("/admin/clients")]);
      setProjects(p.data); setClients(c.data);
    } catch { toast.error("Erreur de chargement"); }
  };
  useEffect(() => { loadAll(); }, []);

  const saveProject = async () => {
    if (!projectForm.name || !projectForm.client_email) { toast.error("Nom et client requis"); return; }
    try {
      if (editingProject === "new") {
        await api.post("/admin/projects", projectForm);
        toast.success("Projet créé");
      } else {
        await api.put(`/admin/projects/${editingProject}`, projectForm);
        toast.success("Projet mis à jour");
      }
      setEditingProject(null); setProjectForm({ name: "", client_email: "", description: "", status: "active" });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Supprimer ce projet et toutes ses tâches/livrables ?")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Supprimé");
    loadAll();
  };

  const createClient = async () => {
    if (!clientForm.name || !clientForm.email) { toast.error("Nom et courriel requis"); return; }
    try {
      const { data } = await api.post("/admin/clients", clientForm);
      setCreatedCreds({ email: data.email, password: data.temp_password });
      setClientForm({ name: "", email: "", company: "", phone: "" });
      toast.success("Client créé · courriel envoyé");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openProject = projects.find((p) => p.id === openProjId);

  return (
    <div data-testid="projects-panel">
      <Tabs defaultValue="projects">
        <TabsList>
          <TabsTrigger value="projects"><FolderKanban className="h-4 w-4 mr-1" />Projets ({projects.length})</TabsTrigger>
          <TabsTrigger value="clients"><Users className="h-4 w-4 mr-1" />Clients ({clients.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-bold text-xl text-[#0B1F3A]">Projets clients</h2>
            <Button onClick={() => { setProjectForm({ name: "", client_email: "", description: "", status: "active" }); setEditingProject("new"); }} className="bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="project-new">
              <Plus className="h-4 w-4 mr-1" /> Nouveau projet
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F7FA] text-left">
                <tr><th className="p-3">Projet</th><th className="p-3">Client</th><th className="p-3">Statut</th><th className="p-3">Tâches</th><th className="p-3">Livrables</th><th className="p-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {projects.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#4B5563]">Aucun projet. Créez-en un.</td></tr>}
                {projects.map((p) => {
                  const tdone = (p.tasks || []).filter((t) => t.status === "done").length;
                  const tpending = (p.deliverables || []).filter((d) => d.status === "pending").length;
                  return (
                    <tr key={p.id} className="border-t border-gray-100">
                      <td className="p-3"><div className="font-medium text-[#0B1F3A]">{p.name}</div><div className="text-xs text-[#4B5563] truncate max-w-xs">{p.description}</div></td>
                      <td className="p-3 text-[#4B5563]">{p.client_email}</td>
                      <td className="p-3"><span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">{PROJ_STATUS.find((s) => s.v === p.status)?.label || p.status}</span></td>
                      <td className="p-3 text-[#4B5563]">{tdone}/{(p.tasks || []).length}</td>
                      <td className="p-3 text-[#4B5563]">{(p.deliverables || []).length}{tpending > 0 && <span className="ml-1 text-orange-600 font-bold">({tpending} en attente)</span>}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => setOpenProjId(p.id)} data-testid={`project-open-${p.id}`}>Ouvrir <ChevronRight className="h-3 w-3 ml-1" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => { setProjectForm({ name: p.name, client_email: p.client_email, description: p.description, status: p.status }); setEditingProject(p.id); }}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteProject(p.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="clients" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-bold text-xl text-[#0B1F3A]">Comptes clients</h2>
            <Button onClick={() => setCreatingClient(true)} className="bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="client-new"><Plus className="h-4 w-4 mr-1" />Nouveau client</Button>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F7FA] text-left">
                <tr><th className="p-3">Nom</th><th className="p-3">Entreprise</th><th className="p-3">Courriel</th><th className="p-3">Téléphone</th><th className="p-3">Créé</th></tr>
              </thead>
              <tbody>
                {clients.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#4B5563]">Aucun client.</td></tr>}
                {clients.map((c) => (
                  <tr key={c.id} className="border-t border-gray-100">
                    <td className="p-3 font-medium text-[#0B1F3A]">{c.name}</td>
                    <td className="p-3 text-[#4B5563]">{c.company || "—"}</td>
                    <td className="p-3 text-[#4B5563]">{c.email}</td>
                    <td className="p-3 text-[#4B5563]">{c.phone || "—"}</td>
                    <td className="p-3 text-[#4B5563]">{new Date(c.created_at).toLocaleDateString("fr-CA")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Project edit dialog */}
      <Dialog open={!!editingProject} onOpenChange={(v) => !v && setEditingProject(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingProject === "new" ? "Nouveau projet" : "Éditer le projet"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom du projet</Label><Input value={projectForm.name} onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })} data-testid="proj-name" /></div>
            <div><Label>Courriel du client</Label>
              <Select value={projectForm.client_email} onValueChange={(v) => setProjectForm({ ...projectForm, client_email: v })}>
                <SelectTrigger data-testid="proj-client"><SelectValue placeholder="Sélectionner un client..." /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.email}>{c.name} ({c.email})</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-[#4B5563] mt-1">Aucun client ? <button onClick={() => { setEditingProject(null); setCreatingClient(true); }} className="text-[#00B8D9] font-semibold hover:underline">Créer un compte client</button></div>
            </div>
            <div><Label>Description</Label><Textarea rows={3} value={projectForm.description} onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })} /></div>
            <div><Label>Statut</Label>
              <Select value={projectForm.status} onValueChange={(v) => setProjectForm({ ...projectForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PROJ_STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <Button variant="outline" onClick={() => setEditingProject(null)}>Annuler</Button>
              <Button onClick={saveProject} className="bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="proj-save">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client create dialog */}
      <Dialog open={creatingClient} onOpenChange={(v) => { if (!v) { setCreatingClient(false); setCreatedCreds(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{createdCreds ? "Client créé ✓" : "Nouveau client"}</DialogTitle></DialogHeader>
          {createdCreds ? (
            <div className="space-y-3">
              <p className="text-sm text-[#4B5563]">Un courriel avec les identifiants a été envoyé. Vous pouvez aussi les partager manuellement :</p>
              <div className="bg-[#F5F7FA] p-4 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <div><div className="text-xs text-[#4B5563]">Courriel</div><div className="font-mono font-semibold text-[#0B1F3A]">{createdCreds.email}</div></div>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(createdCreds.email); toast.success("Copié"); }}><Copy className="h-4 w-4" /></Button>
                </div>
                <div className="flex items-center justify-between border-t border-gray-200 pt-2">
                  <div><div className="text-xs text-[#4B5563]">Mot de passe temporaire</div><div className="font-mono font-semibold text-[#0B1F3A]">{createdCreds.password}</div></div>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(createdCreds.password); toast.success("Copié"); }}><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
              <Button onClick={() => { setCreatingClient(false); setCreatedCreds(null); }} className="w-full bg-[#00B8D9] hover:bg-[#0096B3]">Fermer</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div><Label>Nom complet *</Label><Input value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} data-testid="client-form-name" /></div>
              <div><Label>Courriel *</Label><Input type="email" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} data-testid="client-form-email" /></div>
              <div><Label>Entreprise</Label><Input value={clientForm.company} onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })} /></div>
              <div><Label>Téléphone</Label><Input value={clientForm.phone} onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })} /></div>
              <p className="text-xs text-[#4B5563]">Un mot de passe sera généré automatiquement et envoyé par courriel <Send className="inline h-3 w-3" /></p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setCreatingClient(false)}>Annuler</Button>
                <Button onClick={createClient} className="bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="client-save">Créer le compte</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Project detail dialog (kanban + deliverables management) */}
      <Dialog open={!!openProjId} onOpenChange={(v) => !v && setOpenProjId(null)}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
          {openProject && (
            <ProjectDetailContent project={openProject} onChange={loadAll} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ----- Sub-component: project detail (kanban + deliverables) -----
function ProjectDetailContent({ project, onChange }) {
  const [taskForm, setTaskForm] = useState({ title: "", description: "", status: "todo" });
  const [editingTask, setEditingTask] = useState(null);
  const [delivForm, setDelivForm] = useState({ title: "", description: "", file_url: "" });
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showDelivForm, setShowDelivForm] = useState(false);

  const reload = async () => { await onChange(); };

  const tasksByStatus = STATUS_COLS.reduce((acc, c) => {
    acc[c.id] = (project.tasks || []).filter((t) => t.status === c.id);
    return acc;
  }, {});

  const saveTask = async () => {
    if (!taskForm.title) { toast.error("Titre requis"); return; }
    try {
      if (editingTask) {
        await api.put(`/admin/projects/${project.id}/tasks/${editingTask}`, taskForm);
        toast.success("Tâche mise à jour");
      } else {
        await api.post(`/admin/projects/${project.id}/tasks`, taskForm);
        toast.success("Tâche ajoutée");
      }
      setShowTaskForm(false); setEditingTask(null); setTaskForm({ title: "", description: "", status: "todo" });
      reload();
    } catch { toast.error("Erreur"); }
  };

  const deleteTask = async (tid) => {
    if (!window.confirm("Supprimer cette tâche ?")) return;
    await api.delete(`/admin/projects/${project.id}/tasks/${tid}`);
    reload();
  };

  const moveTask = async (task, newStatus) => {
    await api.put(`/admin/projects/${project.id}/tasks/${task.id}`, { ...task, status: newStatus });
    reload();
  };

  const addDeliverable = async () => {
    if (!delivForm.title) { toast.error("Titre requis"); return; }
    try {
      await api.post(`/admin/projects/${project.id}/deliverables`, delivForm);
      toast.success("Livrable ajouté — client notifié");
      setShowDelivForm(false); setDelivForm({ title: "", description: "", file_url: "" });
      reload();
    } catch { toast.error("Erreur"); }
  };

  const deleteDeliv = async (did) => {
    if (!window.confirm("Supprimer ce livrable ?")) return;
    await api.delete(`/admin/projects/${project.id}/deliverables/${did}`);
    reload();
  };

  return (
    <div>
      <DialogHeader><DialogTitle className="font-heading">{project.name}</DialogTitle></DialogHeader>
      <p className="text-sm text-[#4B5563]">{project.description}</p>
      <p className="text-xs text-[#4B5563] mt-1">Client : <b>{project.client_email}</b></p>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-[#0B1F3A]">Tableau Kanban</h3>
          <Button size="sm" onClick={() => { setEditingTask(null); setTaskForm({ title: "", description: "", status: "todo" }); setShowTaskForm(true); }} className="bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="add-task"><Plus className="h-4 w-4 mr-1" />Tâche</Button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {STATUS_COLS.map((col) => (
            <div key={col.id} className={`${col.color} rounded-lg p-3 min-h-[140px]`}>
              <div className={`text-xs uppercase tracking-wider font-bold mb-3 ${col.text}`}>{col.label} <span className="ml-1 opacity-60">({tasksByStatus[col.id].length})</span></div>
              <div className="space-y-2">
                {tasksByStatus[col.id].map((t) => (
                  <div key={t.id} className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-100" data-testid={`admin-task-${t.id}`}>
                    <div className="font-semibold text-[#0B1F3A] text-sm">{t.title}</div>
                    {t.description && <div className="text-xs text-[#4B5563] mt-1">{t.description}</div>}
                    <div className="flex items-center gap-1 mt-2">
                      <Select value={t.status} onValueChange={(v) => moveTask(t, v)}>
                        <SelectTrigger className="h-7 text-xs flex-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS_COLS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingTask(t.id); setTaskForm(t); setShowTaskForm(true); }}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => deleteTask(t.id)}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-[#0B1F3A]">Livrables</h3>
          <Button size="sm" onClick={() => setShowDelivForm(true)} className="bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="add-deliverable"><Plus className="h-4 w-4 mr-1" />Livrable</Button>
        </div>
        <div className="space-y-2">
          {(project.deliverables || []).length === 0 && <div className="text-sm text-[#4B5563] bg-[#F5F7FA] p-4 rounded-lg text-center">Aucun livrable.</div>}
          {(project.deliverables || []).map((d) => (
            <div key={d.id} className="bg-white border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#0B1F3A]">{d.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${DELIV_STATUS_BADGE[d.status]}`}>{d.status === "pending" ? "En attente" : d.status === "approved" ? "Approuvé" : "Refusé"}</span>
                </div>
                {d.description && <div className="text-xs text-[#4B5563]">{d.description}</div>}
                {d.client_comment && <div className="text-xs italic text-[#4B5563] mt-1">💬 {d.client_comment}</div>}
              </div>
              <Button size="sm" variant="ghost" onClick={() => deleteDeliv(d.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          ))}
        </div>
      </div>

      {/* Task form modal */}
      <Dialog open={showTaskForm} onOpenChange={(v) => { if (!v) { setShowTaskForm(false); setEditingTask(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingTask ? "Éditer la tâche" : "Nouvelle tâche"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titre</Label><Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} data-testid="task-title" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} /></div>
            <div><Label>Colonne</Label>
              <Select value={taskForm.status} onValueChange={(v) => setTaskForm({ ...taskForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_COLS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={saveTask} className="w-full bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="task-save">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deliverable form modal */}
      <Dialog open={showDelivForm} onOpenChange={setShowDelivForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau livrable</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titre</Label><Input value={delivForm.title} onChange={(e) => setDelivForm({ ...delivForm, title: e.target.value })} data-testid="deliv-title" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={delivForm.description} onChange={(e) => setDelivForm({ ...delivForm, description: e.target.value })} /></div>
            <div><Label>URL du fichier / aperçu</Label><Input placeholder="https://figma.com/file/..." value={delivForm.file_url} onChange={(e) => setDelivForm({ ...delivForm, file_url: e.target.value })} /></div>
            <p className="text-xs text-[#4B5563]">Le client recevra un courriel pour valider ce livrable.</p>
            <Button onClick={addDeliverable} className="w-full bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="deliv-save">Ajouter et notifier le client</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
